import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { ArrowLeftRightIcon, SlidersHorizontalIcon } from 'lucide-react';
type Mode = 'adjustment' | 'return';
type Props = {
  mode: Mode;
};
export function StockActions({
  mode
}: Props) {
  const {
    medicines,
    medicineBatches,
    recordStockMovement
  } = useData();
  const [form, setForm] = useState({
    medicineId: '',
    direction: 'in',
    quantity: '',
    batchId: '',
    notes: ''
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const movementType = mode === 'adjustment' ? form.direction === 'in' ? 'Adjustment In' : 'Adjustment Out' : form.direction === 'in' ? 'Return In' : 'Return Out';
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setMessage('');
    setError('');
    const result = recordStockMovement({
      medicineId: form.medicineId,
      quantityDelta: form.direction === 'in' ? Number(form.quantity) : -Number(form.quantity),
      type: movementType,
      batchId: form.batchId || undefined,
      referenceType: mode === 'return' ? 'Return' : 'Manual',
      notes: form.notes
    });
    if (!result.ok) {
      setError(result.error || 'Unable to record stock activity.');
      return;
    }
    setMessage(`${movementType} recorded. On-hand stock and movement history are updated.`);
    setForm({
      medicineId: '',
      direction: 'in',
      quantity: '',
      batchId: '',
      notes: ''
    });
  };
  const batches = medicineBatches.filter(batch => batch.medicineId === form.medicineId && batch.quantity > 0);
  const title = mode === 'adjustment' ? 'Stock Adjustments' : 'Medicine Returns';
  const subtitle = mode === 'adjustment' ? 'Record verified stock corrections with a mandatory transaction trail.' : 'Record medicines returned into stock or issued back to a supplier.';
  return <div className="max-w-3xl space-y-5" data-id="element-4579">
      <div data-id="element-4580">
        <h2 className="text-xl font-bold text-slate-950" data-id="element-4581">{title}</h2>
        <p className="mt-1 text-sm text-slate-500" data-id="element-4582">{subtitle}</p>
      </div>
      <form onSubmit={submit} className="space-y-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-4583">
        {message && <p role="status" className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700" data-id="element-4584">
            {message}
          </p>}
        {error && <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-id="element-4585">
            {error}
          </p>}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-id="element-4586">
          <Select label="Medicine" required value={form.medicineId} onChange={e => setForm({
          ...form,
          medicineId: e.target.value,
          batchId: ''
        })} options={medicines.map(medicine => ({
          value: medicine.id,
          label: `${medicine.name} · ${medicine.currentQuantity} on hand`
        }))} data-id="element-4587" />
          <Select label="Direction" required value={form.direction} onChange={e => setForm({
          ...form,
          direction: e.target.value
        })} options={[{
          value: 'in',
          label: mode === 'return' ? 'Returned into stock' : 'Add stock'
        }, {
          value: 'out',
          label: mode === 'return' ? 'Returned to supplier / issued out' : 'Remove stock'
        }]} data-id="element-4588" />
          <Input label="Quantity" required type="number" min="1" value={form.quantity} onChange={e => setForm({
          ...form,
          quantity: e.target.value
        })} data-id="element-4589" />
          <Select label="Specific batch (optional)" value={form.batchId} onChange={e => setForm({
          ...form,
          batchId: e.target.value
        })} options={[{
          value: '',
          label: form.direction === 'out' ? 'Use earliest-expiring batch' : 'No batch selected'
        }, ...batches.map(batch => ({
          value: batch.id,
          label: `${batch.batchNumber} · ${batch.quantity} available`
        }))]} data-id="element-4590" />
        </div>
        <label className="block text-sm font-medium text-gray-700" data-id="element-4591">
          Reason / notes{' '}
          <textarea required value={form.notes} onChange={e => setForm({
          ...form,
          notes: e.target.value
        })} rows={3} className="mt-1 block w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" placeholder="Explain the correction or return for audit purposes" data-id="element-4592" />
        </label>
        <div className="flex justify-end" data-id="element-4593">
          <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-id="element-4594">
            {mode === 'adjustment' ? <SlidersHorizontalIcon className="mr-2 h-4 w-4" data-id="element-4595" /> : <ArrowLeftRightIcon className="mr-2 h-4 w-4" data-id="element-4596" />}
            {mode === 'adjustment' ? 'Record adjustment' : 'Record return'}
          </Button>
        </div>
      </form>
    </div>;
}